import React, { useState } from 'react';
import PageTitle from '../components/SEO/PageTitle';
import { useAuth } from '../contexts/AuthContext';
import { useBlog } from '../hooks/useBlog';
import BlogCard from '../components/Blog/BlogCard';
import BlogDetailModal from '../components/Blog/BlogDetailModal';
import LoginModal from '../components/Auth/LoginModal';

const Blog = () => {
    const { currentUser } = useAuth();
    const { blogs, loading, error, selectedBlog, loadBlogById, clearSelectedBlog } = useBlog();
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [pendingBlogId, setPendingBlogId] = useState(null);

    const handleReadMore = async (blogId) => {
        if (!currentUser) {
            // Store the blog ID to load after login
            setPendingBlogId(blogId);
            setShowLoginModal(true);
            return;
        }
        
        // User is logged in, load and show full article
        await loadBlogById(blogId);
    };

    const handleLoginSuccess = async () => {
        setShowLoginModal(false);
        // After successful login, load the pending blog
        if (pendingBlogId) {
            await loadBlogById(pendingBlogId);
            setPendingBlogId(null);
        }
    };

    const handleCloseModal = () => {
        clearSelectedBlog();
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <i className="fas fa-spinner fa-spin text-4xl text-gradpath-green"></i>
                <span className="ml-3 text-gray-600">Loading articles...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto px-4 py-16 text-center">
                <div className="bg-red-50 rounded-xl p-8 max-w-md mx-auto">
                    <i className="fas fa-exclamation-triangle text-5xl text-red-500 mb-4"></i>
                    <p className="text-red-600">{error}</p>
                    <button 
                        onClick={() => window.location.reload()}
                        className="mt-4 px-6 py-2 bg-gradpath-green text-white rounded-lg hover:bg-gradpath-dark transition-all"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <PageTitle 
    title="Career Blog" 
    description="Career advice, tips, and insights for graduates and job seekers in South Africa."
/>
            {/* Page Header */}
            <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-2xl mb-4">
                    <i className="fas fa-blog text-white text-2xl"></i>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-gradpath-dark mb-3">
                    Career Blog
                </h1>
                <p className="text-gray-600 max-w-2xl mx-auto">
                    Insights, tips, and advice for your career journey. 
                    {!currentUser && (
                        <span className="block text-sm text-gradpath-green mt-2">
                            <i className="fas fa-lock mr-1"></i> Login to read full articles
                        </span>
                    )}
                </p>
            </div>

            {/* Stats Bar */}
            {blogs.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-4 mb-8 flex justify-between items-center flex-wrap gap-4">
                    <div className="flex items-center gap-2">
                        <i className="fas fa-newspaper text-gradpath-green"></i>
                        <span className="text-gray-600">
                            <strong className="text-gradpath-dark">{blogs.length}</strong> articles available
                        </span>
                    </div>
                    {!currentUser && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <i className="fas fa-lock text-gradpath-green"></i>
                            <span>Login required for full access</span>
                        </div>
                    )}
                </div>
            )}

            {/* Blog Grid */}
            {blogs.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl">
                    <i className="fas fa-newspaper text-6xl text-gray-300 mb-4"></i>
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No Articles Yet</h3>
                    <p className="text-gray-500">Check back soon for new career insights and tips!</p>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {blogs.map(blog => (
                        <BlogCard 
                            key={blog.id} 
                            blog={blog} 
                            onReadMore={handleReadMore}
                        />
                    ))}
                </div>
            )}

            {/* Blog Detail Modal */}
            <BlogDetailModal 
                blog={selectedBlog}
                isOpen={!!selectedBlog}
                onClose={handleCloseModal}
            />

            {/* Login Modal */}
            <LoginModal 
                isOpen={showLoginModal} 
                onClose={() => {
                    setShowLoginModal(false);
                    setPendingBlogId(null);
                }}
                onSwitchToSignup={() => {
                    setShowLoginModal(false);
                    // You can add signup modal trigger here
                }}
            />
        </div>
    );
};

export default Blog;